import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
/**
 * Hook that allows defining a keyboard shortcut to focus an input.
 *
 * @param {useKeyboardShortcutProps} props
 * - {boolean | Keys} shortcut: Optional. Enable or configure a shortcut to focus the input.
 * - {MutableRefObject<HTMLInputElement | undefined>} inputRef: Optional. Reference of the input you want to focus/blur.
 * - {boolean} isOpen: Optional. A boolean indicating whether the input is currently focused.
 * - {(event: KeyboardEvent) => void} onOpen: Optional. Function to run when the shortcut is detected but the input isn't focused.
 * - {(event: KeyboardEvent) => void} onClose: Optional. Function to run when the shortcut is detected and the input is currently focused.
 *
 * @see SearchInput
 *
 * @example
 *
 *  import React from 'react';
 *  import { useKeyboardShortcut, useSearchController } from '@react-admin/ra-search';
 *  import { CardContent } from '@mui/material';
 *  import { Link } from 'react-router-dom';
 *  import { Admin, Layout, Resource } from 'react-admin';
 *  import { dataProvider } from './dataProvider';
 *  import { ArtistsList } from './artists';
 *
 *  const MyDashboard = () => {
 *      const ref = React.useRef<HTMLInputElement>();
 *      useKeyboardShortcut({
 *          shortcut: true,
 *          inputRef: ref,
 *      });
 *      const { searchData, query, doSearch } = useSearchController({});
 *      const onChange = e => doSearch(e.target.value);
 *      return (
 *          <CardContent>
 *              <input type="text" value={query} onChange={onChange} ref={ref} />
 *              {searchData.data?.map(item => (
 *                  <div key={item.id}>
 *                      <Link to={item.url}>
 *                          <strong>{item.content.label}</strong>
 *                      </Link>
 *                      <p>{item.content.description}</p>
 *                  </div>
 *              ))}
 *          </CardContent>
 *      );
 *  };
 *
 *  const App = () => (
 *    <Admin dataProvider={dataProvider} dashboard={MyDashboard}>
 *      <Resource
 *        name="artists"
 *        list={ArtistsList}
 *      />
 *    </Admin>
 *  );
 *
 */
export var useKeyboardShortcut = function (_a) {
    var _b = _a.shortcut, shortcut = _b === void 0 ? true : _b, inputRef = _a.inputRef, isFocused = _a.isFocused, _c = _a.onOpen, onOpen = _c === void 0 ? function (keyboardEvent) {
        var _a;
        keyboardEvent.preventDefault();
        (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    } : _c, onClose = _a.onClose;
    var includesEsc = (Array.isArray(shortcut) &&
        shortcut.some(function (key) { return key.includes('esc'); })) ||
        (typeof shortcut === 'string' && shortcut.includes('esc'));
    useHotkeys(shortcut === false ? '' : shortcut === true ? 'mod+k' : shortcut, function (keyboardEvent) {
        var _a;
        if (isFocused
            ? isFocused
            : (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === document.activeElement) {
            onClose && onClose(keyboardEvent);
            (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            return;
        }
        onOpen(keyboardEvent);
    }, {
        enabled: !!shortcut,
        enableOnFormTags: ['input'],
    });
    useEffect(function () {
        var handleKeyDown = function (event) {
            var _a;
            if (event.key === 'Escape') {
                onClose && onClose(event);
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            }
        };
        if (includesEsc || !shortcut)
            return;
        document.addEventListener('keydown', handleKeyDown);
        return function () {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, includesEsc, shortcut, inputRef]);
};
